import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import {
  TIPOS_DOCUMENTO,
  TIPOS_PARENTESCO,
  ESTADOS_PROCESO,
  validateEmail,
  validateDocumento,
  validateEdad,
  validateRadicado,
} from "../../utils/adicionalesUtils";

function EditAdicionalModal({ open, onClose, onSubmit, adicional, procesoData }) {
  // Agregar procesoData aquí
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (adicional && open) {
      let initialData = { ...adicional };

      // Si es proceso judicial y no tiene demandado, usar el nombre del cliente
      if (
        adicional.tipo_adicional === "PROCESO_JUDICIAL" &&
        procesoData?.cliente_detalle &&
        !adicional.demandado
      ) {
        initialData.demandado = `${procesoData.cliente_detalle.nombres || ""} ${
          procesoData.cliente_detalle.apellidos || ""
        }`.trim();
      }

      setFormData(initialData);
      setErrors({});
    }
  }, [adicional, open, procesoData]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.tipo_adicional === "PERSONA_CARGO") {
      if (!formData.nombre_completo) newErrors.nombre_completo = "Campo requerido";
      if (!validateDocumento(formData.numero_documento, formData.tipo_documento)) {
        newErrors.numero_documento = "Documento inválido";
      }
      if (!validateEdad(formData.edad)) newErrors.edad = "Edad inválida";
      if (!formData.parentesco) newErrors.parentesco = "Campo requerido";
    }

    if (formData.tipo_adicional === "SOCIEDADES_PROCESO") {
      if (!validateDocumento(formData.numero_documento, formData.tipo_documento)) {
        newErrors.numero_documento = "Documento inválido";
      }
      if (!formData.tipo_documento) newErrors.tipo_documento = "Campo requerido";
      if (!formData.numero_documento) newErrors.numero_documento = "Campo requerido";
      if (!formData.nombre_completo) newErrors.nombre_completo = "Campo requerido";
      if (!formData.direccion_notificacion) newErrors.direccion_notificacion = "Campo requerido";
    }

    if (formData.tipo_adicional === "PROCESO_JUDICIAL") {
      if (!formData.juzgado) newErrors.juzgado = "Campo requerido";
      if (!validateEmail(formData.email)) newErrors.email = "Correo inválido";
      if (!formData.demandante) newErrors.demandante = "Campo requerido";
      if (!formData.demandado) newErrors.demandado = "Campo requerido";
      if (!validateRadicado(formData.radicado)) newErrors.radicado = "Formato inválido";
      if (!formData.estado_actual) newErrors.estado_actual = "Campo requerido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    onSubmit(formData);
    onClose();
  };

  const renderPersonaCargoFields = () => (
    <>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={4}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Nombre Completo
            </SuiTypography>
            <SuiInput
              error={!!errors.nombre_completo}
              helperText={errors.nombre_completo}
              value={formData.nombre_completo}
              onChange={handleChange("nombre_completo")}
            />
          </SuiBox>
        </Grid>
        <Grid size={4}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Tipo Documento
            </SuiTypography>
            <Select value={formData.tipo_documento} onChange={handleChange("tipo_documento")}>
              {TIPOS_DOCUMENTO.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </MenuItem>
              ))}
            </Select>
          </SuiBox>
        </Grid>
        <Grid size={4}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Número Documento
            </SuiTypography>
            <SuiInput
              error={!!errors.numero_documento}
              helperText={errors.numero_documento}
              value={formData.numero_documento}
              onChange={handleChange("numero_documento")}
            />
          </SuiBox>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Parentesco
            </SuiTypography>
            <Select value={formData.parentesco} onChange={handleChange("parentesco")}>
              {TIPOS_PARENTESCO.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </MenuItem>
              ))}
            </Select>
          </SuiBox>
        </Grid>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Edad
            </SuiTypography>
            <SuiInput
              error={!!errors.edad}
              helperText={errors.edad}
              value={formData.edad}
              onChange={handleChange("edad")}
              type="number"
            />
          </SuiBox>
        </Grid>
      </Grid>
    </>
  );

  const renderSociedadesProcesoFields = () => (
    <>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Tipo Documento
            </SuiTypography>
            <Select value={formData.tipo_documento} onChange={handleChange("tipo_documento")}>
              {TIPOS_DOCUMENTO.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </MenuItem>
              ))}
            </Select>
          </SuiBox>
        </Grid>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Identificación
            </SuiTypography>
            <SuiInput
              error={!!errors.numero_documento}
              helperText={errors.numero_documento}
              value={formData.numero_documento}
              onChange={handleChange("numero_documento")}
            />
          </SuiBox>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Nombre
            </SuiTypography>
            <SuiInput
              error={!!errors.nombre_completo}
              helperText={errors.nombre_completo}
              value={formData.nombre_completo}
              onChange={handleChange("nombre_completo")}
            />
          </SuiBox>
        </Grid>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Dirección Notificación
            </SuiTypography>
            <SuiInput
              error={!!errors.direccion_notificacion}
              helperText={errors.direccion_notificacion}
              value={formData.direccion_notificacion}
              onChange={handleChange("direccion_notificacion")}
            />
          </SuiBox>
        </Grid>
      </Grid>
    </>
  );

  const renderProcesoJudicialFields = () => (
    <>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Juzgado
            </SuiTypography>
            <SuiInput
              error={!!errors.juzgado}
              helperText={errors.juzgado}
              value={formData.juzgado}
              onChange={handleChange("juzgado")}
            />
          </SuiBox>
        </Grid>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Correo Electrónico
            </SuiTypography>
            <SuiInput
              error={!!errors.email}
              helperText={errors.email}
              value={formData.email}
              onChange={handleChange("email")}
            />
          </SuiBox>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Demandante
            </SuiTypography>
            <SuiInput
              error={!!errors.demandante}
              helperText={errors.demandante}
              value={formData.demandante}
              onChange={handleChange("demandante")}
            />
          </SuiBox>
        </Grid>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Demandado
            </SuiTypography>
            <SuiInput
              error={!!errors.demandado}
              helperText={errors.demandado}
              value={formData.demandado}
              onChange={handleChange("demandado")}
              placeholder={
                procesoData?.cliente_detalle
                  ? `${procesoData.cliente_detalle.nombres} ${procesoData.cliente_detalle.apellidos}`.trim()
                  : "Nombre del demandado"
              }
            />
          </SuiBox>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={12}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Radicado{" "}
              <span style={{ fontSize: "12px", color: "#889fccff", minWidth: "35px" }}>
                (debe tener 23 caracteres, sin contar los guiones)
              </span>
            </SuiTypography>
            <SuiInput
              error={!!errors.radicado}
              helperText={errors.radicado}
              value={formData.radicado}
              onChange={handleChange("radicado")}
              placeholder="(XXXXXXXXXXXX-XX-XX-XXX-XXXX)"
            />
          </SuiBox>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Estado Actual
            </SuiTypography>
            <Select value={formData.estado_actual} onChange={handleChange("estado_actual")}>
              {ESTADOS_PROCESO.map((estado) => (
                <MenuItem key={estado.value} value={estado.value}>
                  {estado.label}
                </MenuItem>
              ))}
            </Select>
          </SuiBox>
        </Grid>
        <Grid item size={6}>
          <SuiTypography variant="h6" fontWeight="dark" sx={{ mt: 2 }}>
            <Checkbox
              checked={formData.mostrar_nota}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  mostrar_nota: e.target.checked,
                }));
              }}
            />
            {"  "}
            ¿Incluir la Nota: Se deja constancia de que a la fecha de...?
          </SuiTypography>
        </Grid>
      </Grid>
    </>
  );

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case "PERSONA_CARGO":
        return "Persona a Cargo";
      case "SOCIEDADES_PROCESO":
        return "Sociedades";
      case "PROCESO_JUDICIAL":
        return "Proceso Judicial";
      default:
        return tipo;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <SuiTypography variant="h6">Editar {getTipoLabel(formData.tipo_adicional)}</SuiTypography>
      </DialogTitle>
      <DialogContent>
        <SuiBox py={1}>
          {formData.tipo_adicional === "PERSONA_CARGO" && renderPersonaCargoFields()}
          {formData.tipo_adicional === "SOCIEDADES_PROCESO" && renderSociedadesProcesoFields()}
          {formData.tipo_adicional === "PROCESO_JUDICIAL" && renderProcesoJudicialFields()}
        </SuiBox>
      </DialogContent>
      <DialogActions>
        <SuiButton onClick={onClose} buttonColor="light">
          Cancelar
        </SuiButton>
        <SuiButton onClick={handleSubmit} variant="gradient" buttonColor="dark">
          Guardar Cambios
        </SuiButton>
      </DialogActions>
    </Dialog>
  );
}

EditAdicionalModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  procesoData: PropTypes.shape({
    // Agregar la validación del prop
    cliente_detalle: PropTypes.shape({
      nombres: PropTypes.string,
      apellidos: PropTypes.string,
    }),
  }),
  adicional: PropTypes.shape({
    id: PropTypes.number.isRequired,
    tipo_adicional: PropTypes.oneOf(["PERSONA_CARGO", "SOCIEDADES_PROCESO", "PROCESO_JUDICIAL"])
      .isRequired,
    tipo_documento: PropTypes.string,
    numero_documento: PropTypes.string,
    nombre_completo: PropTypes.string,
    parentesco: PropTypes.string,
    edad: PropTypes.number,
    direccion_notificacion: PropTypes.string,
    juzgado: PropTypes.string,
    email: PropTypes.string,
    demandante: PropTypes.string,
    demandado: PropTypes.string,
    radicado: PropTypes.string,
    estado_actual: PropTypes.string,
    mostrar_nota: PropTypes.bool,
  }),
};

export default EditAdicionalModal;
